import { useMentiSpring } from '@mentimeter/motion/spring';
import type { ScaleLinear } from 'd3-scale';
import { animated, useSpring } from '@react-spring/web';
import { getContrastingTextColor } from '../utils/colors';
import { BAR_HEIGHT_FACTOR, getAverageScoreCircleRadius } from './constants';
import { getWeightedAverage } from './helpers';
import type { ScalesScore } from './Scales';

interface ScalesAverageScoreProps {
  scores: ScalesScore;
  y: number;
  fill: string;
  xScale: ScaleLinear<number, number>;
  height: number;
  totalWidth: number;
  shouldShowAverageCircle: boolean;
  placeholderBarColor: string;
  disableAnimations?: boolean;
}

const CIRCLE_PADDING = 5;
const PLACEHOLDER_BAR_OPACITY = 0.1;

const roundToOneDecimal = (value: number) => {
  return Math.round(value * 10) / 10;
};

export const ScalesAverageScore = ({
  y,
  scores,
  fill,
  xScale,
  height,
  totalWidth,
  shouldShowAverageCircle,
  placeholderBarColor,
  disableAnimations = false,
}: ScalesAverageScoreProps) => {
  const weightedAverage = getWeightedAverage(scores);
  const average = roundToOneDecimal(weightedAverage);

  const circleRadius = getAverageScoreCircleRadius(height);
  const barHeight = height * BAR_HEIGHT_FACTOR;
  const borderRadius = barHeight * 0.3;

  const circleOffset = circleRadius + CIRCLE_PADDING;
  const offset = shouldShowAverageCircle ? 0 : circleOffset;

  const [styles] = useMentiSpring({
    from: { x: 0, width: 0, xRightSide: 0, widthRightSide: 0, fullWidth: 0 },
    to: {
      x: xScale(average),
      width: Math.max(xScale(average) - offset, 0),
      xRightSide: xScale(average) + offset,
      widthRightSide: Math.max(totalWidth - xScale(average) - offset, 0),
      fullWidth: Math.max(totalWidth - xScale(average), 0),
    },
    immediate: disableAnimations,
  });

  const { r, opacity } = useSpring({
    r: shouldShowAverageCircle ? circleRadius * 0.8 : circleRadius,
    opacity: shouldShowAverageCircle ? 0 : 1,
    immediate: disableAnimations,
  });

  const hasOnlyZeros = Object.values(scores).every((score) => score === 0);
  if (hasOnlyZeros) {
    return (
      <animated.rect
        data-testid="placeholder-bar"
        y={y - barHeight}
        x={0}
        width={totalWidth}
        height={barHeight}
        fill={placeholderBarColor}
        opacity={PLACEHOLDER_BAR_OPACITY}
        rx={borderRadius}
      />
    );
  }

  const numberOfDigits = average.toFixed(0).length;
  const textLengthFontSizeRatio = numberOfDigits >= 3 ? 0.75 : 1;

  return (
    <g data-testid="scales-average-score">
      <animated.rect
        data-testid="placeholder-bar"
        y={y - barHeight}
        x={styles.xRightSide}
        width={styles.widthRightSide}
        height={barHeight}
        fill={placeholderBarColor}
        opacity={PLACEHOLDER_BAR_OPACITY}
        rx={borderRadius}
      />
      <animated.rect
        data-testid="results-bar"
        y={y - barHeight}
        width={styles.width}
        height={barHeight}
        fill={fill}
        rx={borderRadius}
      />
      <animated.rect
        data-testid="hover-area-exapander"
        opacity={0}
        y={y - barHeight / 2}
        height={r}
        width={totalWidth}
      />
      <animated.circle
        cx={styles.x}
        cy={y - barHeight / 2}
        r={r}
        fill={fill}
        opacity={opacity}
      />
      <animated.text
        x={styles.x}
        y={y - barHeight / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fill={getContrastingTextColor(fill)}
        fontSize={circleRadius * 0.9 * textLengthFontSizeRatio}
        fontWeight="bold"
        data-testid="average-score"
        opacity={opacity}
      >
        {average.toFixed(1)}
      </animated.text>
    </g>
  );
};
